import dynamic from "next/dynamic";
import Link from "next/link";
import Head from "next/head";
import { useState } from "react";
import Footer from "../components/Footer";
import ContactUsHome from "../components/homepage/ContactUsHome";
import { CustomerSection } from "../components/ai-agent-demo/CustomerSection";
import BlogSlider from "../components/reusable/BlogSlider";

const Navbar = dynamic(() => import("../components/Navbar"), { ssr: false });

/* ─── data ──────────────────────────────────────────────── */

const industryIntelligence = [
  {
    name: "Banking & Financial Services",
    link: "/industry/banking",
    examples: [
      {
        company: "JPMorgan",
        description:
          "Deployed LLM Suite to 50,000+ employees across investment banking and wealth management — contract analysis, research summarisation, and compliance review. Cited by CEO Jamie Dimon in shareholder letters as one of the most extensive enterprise LLM deployments in financial services.",
      },
      {
        company: "Klarna",
        description:
          "AI assistant handles the equivalent workload of 700 full-time customer service agents. Handled two-thirds of all customer service chats in its first month — February 2024 public announcement.",
      },
      {
        company: "Morgan Stanley",
        description:
          "AI-powered wealth management assistant deployed to 16,000+ financial advisors, built with OpenAI. Provides instant access to research, product information, and compliance documentation during client calls.",
      },
    ],
    itmtbPattern:
      "Mid-market Indian banks: RBI Digital Lending compliance monitoring agents, vernacular customer support agents, fraud detection on transaction streams. Regulatory architecture is co-designed from the first sprint.",
  },
  {
    name: "Insurance",
    link: "/industry/insurance",
    examples: [
      {
        company: "Lemonade",
        description:
          "AI handles straightforward claims end-to-end — document review, fraud check, settlement — in seconds. Extensively documented in their S-1 and earnings calls as a core operational differentiator.",
      },
      {
        company: "Tractable",
        description:
          "Computer vision for vehicle damage assessment from photographs, used by global insurers to replace manual adjusters on standard claims. Sub-minute assessment on clear-image submissions.",
      },
      {
        company: "Global Tier-1 insurers",
        description:
          "AI-assisted underwriting decision support is in production at multiple Tier-1 insurers in Europe and North America — reviewing risk signals, flagging edge cases, and preparing recommendations for human underwriters while maintaining regulatory sign-off.",
      },
    ],
    itmtbPattern:
      "Indian insurance: IRDAI-compliant underwriting decision support, IIB data reporting agents, claims automation. The compliance posture document is the first deliverable of the architecture sprint.",
  },
  {
    name: "Life Sciences & Pharma",
    link: "/industry/life-sciences",
    examples: [
      {
        company: "Pfizer",
        description:
          "PfizerGPT — internal LLM deployment for research summarisation, clinical document review, and scientific literature analysis. Deployed across R&D and regulatory functions, announced 2023.",
      },
      {
        company: "Recursion Pharmaceuticals",
        description:
          "AI-native drug discovery company (NASDAQ: RXRX). Foundation models trained on biological data identify novel drug targets — compressing discovery timelines that traditionally run 5–10 years.",
      },
      {
        company: "Insilico Medicine",
        description:
          "A generative AI system designed novel drug candidate INS018_055 for idiopathic pulmonary fibrosis. Entered Phase II clinical trials — the first AI-designed drug candidate to reach this milestone (published in Nature Biotechnology).",
      },
    ],
    itmtbPattern:
      "Pharma and CROs: formulation assistants built for CDSCO and FDA 21 CFR Part 11 audit-trail requirements, regulatory document automation, R&D literature agents. Compliance architecture is co-designed with regulatory counsel.",
  },
  {
    name: "Retail, D2C & Quick Commerce",
    link: "/industry/retail",
    examples: [
      {
        company: "Walmart",
        description:
          "Multiple AI deployments across supplier negotiation agents, in-app shopping assistant, and store operations optimisation. Walmart has made AI a stated strategic priority with publicly documented investments across the retail stack.",
      },
      {
        company: "Amazon",
        description:
          "Rufus — AI shopping assistant trained on Amazon's product catalogue, customer reviews, and community Q&A. Launched in the US in 2024. Handles product discovery, comparison, and decision support natively in the app.",
      },
      {
        company: "Instacart",
        description:
          "Caper AI smart carts with on-device AI, plus AI-powered search and recipe-to-cart agents. Instacart has published extensively on their AI product roadmap including agentic shopping flows.",
      },
    ],
    itmtbPattern:
      "Quick commerce and D2C: dark-store inventory rebalancing agents, demand-forecasting on 10-minute SKU windows, dynamic-pricing agents for hyperlocal demand. Integrates with existing WMS and POS systems.",
  },
  {
    name: "Supply Chain & Logistics",
    link: "/industry/supply-chain",
    examples: [
      {
        company: "Maersk",
        description:
          "Extensive AI investments in container routing, predictive maintenance, and port operations optimisation — documented across investor materials, sustainability reports, and conference talks over 2022–2024.",
      },
      {
        company: "DHL",
        description:
          "AI and robotics deployment across parcel sorting, predictive shipment risk, and last-mile optimisation. DHL Trend Research publishes annually on AI adoption progress across the logistics stack.",
      },
      {
        company: "Flexport",
        description:
          "AI-powered freight orchestration — rate recommendations, documentation automation, and exception management. Flexport has positioned AI as a stated strategic differentiator in its platform and public communications.",
      },
    ],
    itmtbPattern:
      "Our deployment: custom CNN for SKU identification across 100,000+ SKUs in 35 countries — 97% reduction in identification time, sub-3-minute lookup. Multi-site reconciliation agents and ERP integration (Dynamics 365, SAP) running in production.",
  },
  {
    name: "Professional Services & Accounting",
    link: null,
    examples: [
      {
        company: "KPMG",
        description:
          "KPMG Clara — AI-powered audit platform used in live engagements. Analyses contract terms, flags anomalies in financial data, and surfaces audit risks. Deployed at scale across KPMG's global audit practice.",
      },
      {
        company: "Harvey AI",
        description:
          "Legal AI used by Am Law 200 firms and Big 4 accounting practices for research, contract review, and regulatory analysis. One of the most widely adopted specialised legal AI systems in professional services.",
      },
      {
        company: "EY",
        description:
          "EY.ai — enterprise AI platform announced in 2023 with a $1.4 billion investment. Deployed across audit, tax, and advisory practices for document analysis, research, and client-facing workflow automation.",
      },
    ],
    itmtbPattern:
      "Mid-market professional services: month-end close reconciliation agents (GL ingestion → variance report), audit working-paper preparation, GST and international tax research agents. Pattern is typically agentic RAG over historical files and regulatory sources.",
  },
];

const proofCases = [
  {
    label: "Supply chain",
    problem:
      "A global supply chain leader needed to identify 100,000+ SKUs across 35 countries without manual lookup — a process taking up to 3 weeks per reconciliation cycle.",
    approach:
      "Architecture sprint identified a custom CNN with a serverless inference layer as the right pattern. Data pipeline scoped, compliance posture mapped, edge cases documented before a line of build code was written.",
    outcome: "97% reduction in SKU identification time. Sub-3-minute lookup per item across 35 countries. Running in production.",
    link: "/industry/supply-chain",
  },
  {
    label: "Risk intelligence",
    problem:
      "A global intelligence and risk consulting firm needed to compress 3-day third-party risk analyses — document discovery, financial review, regulatory checks, synthesis — without adding headcount.",
    approach:
      "Architecture sprint determined four specialised AI models in orchestration was the correct pattern. Each model handles one domain; outputs synthesised by a final reasoning layer.",
    outcome: "Analyses that took 3 days now complete in under 1 hour with minimal human input. Running in production.",
    link: "/blog/business-intelligence-ai-strategy",
  },
  {
    label: "Managed services",
    problem:
      "ITMTB needed to scale its own managed services operation without linear headcount growth. Incident triage and routine remediation were the primary bottlenecks.",
    approach:
      "Architecture sprint mapped 40+ incident categories and remediation patterns. Orchestrik agents built to triage, execute routine remediations, and escalate with full context.",
    outcome: "Technology support effort reduced by over 90%. Orchestrik now operates as a standalone AI operations platform.",
    link: "/orchestrik",
    external: false,
  },
];

const deliverables = [
  {
    number: "01",
    title: "AI opportunity map",
    body: "Every workflow candidate scored on ROI potential × implementation feasibility × regulatory risk. Ranked. Scored against your specific business model and tech stack — not a generic framework.",
  },
  {
    number: "02",
    title: "Priority workflow shortlist",
    body: "The 3–5 workflows with the best risk-adjusted return and the clearest implementation path. Each entry includes: data requirements, integration surface, agent pattern, and estimated production timeline.",
  },
  {
    number: "03",
    title: "Build / buy / partner recommendation",
    body: "Per workflow: should you build custom, configure an existing platform, or partner for a specific capability? We have no platform to sell. The recommendation is independent.",
  },
  {
    number: "04",
    title: "90-day pilot scope",
    body: "A scoped, de-risked first phase you can hand to engineering — or to us. Bounded objective, defined success metrics, go/no-go checkpoint. A plan engineering can actually execute, not a vague roadmap.",
  },
];

const differentiators = [
  {
    claim: "We own the build risk if you continue with us",
    detail:
      "When the diagnostic produces a pilot scope and you choose to engage us for the build, we carry the delivery risk. One team from diagnostic to production — not a handoff to a separate team who never read the strategy document.",
  },
  {
    claim: "Recommendations are technically vetted, not abstract",
    detail:
      "Every roadmap item is validated against real integration surfaces — your ERP, your data infrastructure, your existing APIs. Recommendations that cannot survive contact with your tech stack do not go in the document.",
  },
  {
    claim: "We have shipped the patterns we recommend",
    detail:
      "The agent workflows we recommend are ones we have built in production — not patterns from a Gartner report. If we recommend multi-agent orchestration for your risk reporting, it is because we have shipped one.",
  },
];

const governanceAnchors = [
  {
    title: "Regulatory posture (India)",
    body: "RBI Digital Lending, SEBI, IRDAI, IIB, DPDP consent, CDSCO / FDA 21 CFR Part 11 — documented and designed into your AI architecture from the first sprint, not retrofitted.",
  },
  {
    title: "Model risk management",
    body: "Every recommended AI system includes: non-determinism monitoring, audit trail requirements, escalation paths, and acceptable-outcome distributions for regulated decisions.",
  },
  {
    title: "Vendor lock-in",
    body: "We surface lock-in risk in every build / buy / partner recommendation. Where vendor dependencies are unavoidable, we document the exit path and portability of your data and models.",
  },
];

const faqItems = [
  {
    question: "How long does an AI strategy engagement take?",
    answer:
      "The core diagnostic is two weeks, fixed-price. Week one: a focused executive session, workflow interviews, and technical environment review. Week two: analysis, scoring, and deliverable preparation. You receive four deliverables at the end of week two. If you proceed to a production build, the architecture sprint and build timeline follow immediately — no context reset.",
  },
  {
    question: "What does an AI roadmap actually contain?",
    answer:
      "Our roadmap is four documents, not a slide deck: (1) an AI opportunity map with every workflow candidate scored on ROI × feasibility × regulatory risk; (2) a priority shortlist with data requirements and integration notes per workflow; (3) a build / buy / partner recommendation per workflow; (4) a 90-day pilot scope for the top priority — objective, success metrics, go/no-go checkpoint, and estimated cost. Total: 15–30 pages, no filler.",
  },
  {
    question: "How do we measure AI ROI before building anything?",
    answer:
      "We measure against two axes: time and cost saved on the current workflow (labour hours, error rate, cycle time), and revenue or margin impact enabled (faster decisions, better pricing, higher conversion). The baseline is your current workflow cost; the projection is the post-agent workflow cost plus implementation cost amortised over 24 months. We do not use vanity metrics — AI adoption rate or model accuracy in isolation tell you nothing about business impact.",
  },
  {
    question: "Build vs buy: when does each make sense?",
    answer:
      "Buy when: the workflow is standard, the vendor has your regulatory context built in, and the integration surface is clean. Build when: the workflow is proprietary, the data is sensitive, the regulatory posture is complex, or the competitive advantage is in the process itself. We recommend build for workflows that touch proprietary data or regulated decisions in Indian financial services — generic platforms rarely carry RBI, IRDAI, or DPDP context out of the box.",
  },
  {
    question: "How do we govern AI without slowing everything down?",
    answer:
      "Governance is scoped to risk, not applied uniformly. Low-risk, non-customer-facing agents (internal reconciliation, research summaries, document drafting) need lightweight review — audit trail and human spot-check. High-risk, regulated decisions (credit, claims, clinical) need formal model risk management: defined acceptable-outcome distributions, mandatory HITL, rollback paths, and regulator-ready audit trails. We document the governance posture per workflow in the diagnostic.",
  },
  {
    question: "What if our data is not ready for AI?",
    answer:
      "Most organisations have better data than they think — and worse data hygiene than they realise. The diagnostic includes a data readiness assessment per workflow candidate. Where data is insufficient, we document what is needed — not a multi-year data lake project, usually a targeted 90-day cleanup on a specific domain. We will not recommend building an agent on a workflow where the data cannot support it. That is how agent projects fail.",
  },
  {
    question: "What is a realistic pilot timeline?",
    answer:
      "A bounded pilot — one workflow, one agent pattern, one integration — typically runs 6–10 weeks from the end of the architecture sprint. This produces a working production agent, not a demo. Success metrics are defined before build starts; the go/no-go checkpoint is at week 4. If the pilot does not meet its metric, we surface the reasons and the options — not a project extension and a new invoice.",
  },
  {
    question: "How do we manage AI change in a large organisation?",
    answer:
      "AI change management fails when treated as a communications exercise. We address it as an organisational design problem: who owns the agent's outputs, what happens when it is wrong, how errors are corrected, and what the agent replaces versus augments. The diagnostic includes a change management brief per priority workflow — roles, escalation paths, training requirements, and the rollback plan if adoption stalls.",
  },
];

const relatedPosts = [
  { title: "Agentic AI vs Automation — A CXO's Guide", slug: "agentic-ai-vs-automation", img: "/blogImages/hero/agentic-ai-vs-automation.webp" },
  { title: "What Decision Makers Should Know About Agentic AI in 2025", slug: "what-decision-makers-should-know-about-agentic-ai-in-2025", img: "/blogImages/hero/decision-makers-2025.webp" },
  { title: "Solution Architecture using Agentic AI", slug: "solution-architecture-using-agentic-ai", img: "/blogImages/hero/solution-architecture-agentic-ai.webp" },
  { title: "Agentic AI Pilot Success", slug: "agentic-ai-pilot-success", img: "/blogImages/hero/agentic-ai-pilot.webp" },
  { title: "Agentic AI in Hospitality: Use Cases & Impact", slug: "agentic-ai-hospitality", img: "/blogImages/hero/agentic-ai-hos.webp" },
  { title: "Unleashing Agentic AI: Supply Chain, Fintech and Pharma", slug: "unleashing-agentic-ai-transforming-supply-chain-fintech-and-pharma", img: "/blogImages/hero/Unleashing-Agentic-AI-Transforming-Supply-Chain-Fintech-and-Pharma.webp" },
];

/* ─── schema ─────────────────────────────────────────────── */

const schemaGraph = [
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.itmtb.com" },
      { "@type": "ListItem", position: 2, name: "AI Strategy for Business", item: "https://www.itmtb.com/ai-strategy-for-business" },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.itmtb.com/ai-strategy-for-business#webpage",
    url: "https://www.itmtb.com/ai-strategy-for-business",
    name: "AI Strategy for Business | ITMTB",
    description:
      "ITMTB delivers AI strategy for business leaders — a two-week executive diagnostic, an AI opportunity map ranked by ROI, and an optional path to production build. For CEOs and executives scoping their AI roadmap.",
    publisher: {
      "@type": "Organization",
      name: "ITMTB",
      url: "https://www.itmtb.com",
      logo: { "@type": "ImageObject", url: "https://www.itmtb.com/logo.png" },
      sameAs: ["https://www.linkedin.com/company/itmtb/", "https://twitter.com/ItmtbT"],
    },
    mainEntity: {
      "@type": "Service",
      name: "AI Strategy for Business",
      provider: { "@type": "Organization", name: "ITMTB", url: "https://www.itmtb.com" },
      areaServed: { "@type": "Place", name: "India and Global" },
      serviceType: "AI Strategy Consulting and Executive Diagnostic",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  },
];

/* ─── sub-components ─────────────────────────────────────── */

const SectionEyebrow = ({ label }) => (
  <p className="text-amber-700 text-xs font-semibold uppercase tracking-widest mb-3">{label}</p>
);

const FaqAccordion = ({ items }) => {
  const [open, setOpen] = useState(null);
  return (
    <div>
      {items.map((item, i) => (
        <div key={i} className="border-b border-slate-200">
          <button
            onClick={() => setOpen(open === i ? null : i)}
            className="w-full flex justify-between items-start gap-4 text-left py-5 focus:outline-none group"
            aria-expanded={open === i}
          >
            <span className="text-base font-semibold text-slate-900 leading-snug">{item.question}</span>
            <span className="flex-shrink-0 text-slate-400 group-hover:text-slate-600 transition-colors text-xl leading-none mt-0.5">
              {open === i ? "−" : "+"}
            </span>
          </button>
          <div className={`pb-5 ${open === i ? "block" : "hidden"}`}>
            <p className="text-slate-600 text-sm leading-relaxed">{item.answer}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

/* ─── hero illustration ──────────────────────────────────── */

const RoadmapSVG = () => (
  <svg viewBox="0 0 440 360" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-md opacity-60">
    {/* grid dots */}
    {[...Array(5)].map((_, row) =>
      [...Array(7)].map((_, col) => (
        <circle key={`${row}-${col}`} cx={30 + col * 64} cy={40 + row * 68} r="1.5" fill="#292116" />
      ))
    )}
    {/* concentric arcs — strategic rings */}
    <path d="M 380 180 A 80 80 0 0 0 300 100" stroke="#292116" strokeWidth="1"/>
    <path d="M 380 180 A 140 140 0 0 0 240 40" stroke="#292116" strokeWidth="1"/>
    <path d="M 380 180 A 200 200 0 0 0 180 0" stroke="#292116" strokeWidth="1"/>
    {/* connecting path through milestones */}
    <path d="M 60 280 C 100 260 130 240 160 220 C 200 195 220 185 260 175 C 300 165 330 162 380 160" stroke="#475569" strokeWidth="1.5" strokeDasharray="4 3"/>
    {/* milestone 1 — discovery */}
    <circle cx="80" cy="272" r="22" stroke="#334155" strokeWidth="1.5"/>
    <circle cx="80" cy="272" r="8" stroke="#334155" strokeWidth="1"/>
    {/* milestone 2 — strategy */}
    <circle cx="220" cy="188" r="22" stroke="#475569" strokeWidth="1.5"/>
    <circle cx="220" cy="188" r="8" stroke="#475569" strokeWidth="1"/>
    {/* milestone 3 — execution (amber) */}
    <circle cx="360" cy="158" r="28" stroke="#d97706" strokeWidth="2"/>
    <circle cx="360" cy="158" r="12" stroke="#d97706" strokeWidth="1.5" strokeOpacity="0.5"/>
    <circle cx="360" cy="158" r="4" fill="#d97706" fillOpacity="0.6"/>
    {/* phase labels — short tick marks above each circle */}
    <line x1="80" y1="246" x2="80" y2="238" stroke="#334155" strokeWidth="1.5"/>
    <line x1="220" y1="162" x2="220" y2="154" stroke="#475569" strokeWidth="1.5"/>
    <line x1="360" y1="126" x2="360" y2="116" stroke="#d97706" strokeWidth="1.5"/>
    {/* horizontal guide lines beside each milestone */}
    <line x1="106" y1="260" x2="150" y2="240" stroke="#292116" strokeWidth="1"/>
    <line x1="106" y1="272" x2="155" y2="272" stroke="#292116" strokeWidth="1"/>
    <line x1="246" y1="176" x2="290" y2="162" stroke="#292116" strokeWidth="1"/>
    <line x1="246" y1="188" x2="295" y2="188" stroke="#292116" strokeWidth="1"/>
  </svg>
);

/* ─── page ───────────────────────────────────────────────── */

const AIStrategyPage = () => {
  const [openIndustry, setOpenIndustry] = useState(0);
  return (
  <div className="w-screen bg-white text-slate-900">
    <Navbar />

    <Head>
      <title>AI Strategy for Business | ITMTB</title>
      <meta
        name="description"
        content="ITMTB delivers AI strategy for business leaders — a two-week executive diagnostic, an AI opportunity map ranked by ROI, and an optional path to production build. For CEOs and executives scoping their AI roadmap."
      />
      <meta
        name="keywords"
        content="ai strategy for business, enterprise ai strategy consulting, ai transformation consulting, ai roadmap, ai consulting company india, ai opportunity assessment, enterprise ai roadmap, ai advisory"
      />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="AI Strategy for Business | ITMTB" />
      <meta
        property="og:description"
        content="Two-week executive diagnostic + AI opportunity map for business leaders. Written by people who ship the agents — not a generic strategy deck."
      />
      <meta property="og:image" content="https://www.itmtb.com/logo.png" />
      <meta property="og:url" content="https://www.itmtb.com/ai-strategy-for-business" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="AI Strategy for Business | ITMTB" />
      <meta name="twitter:description" content="AI strategy for executives — two-week diagnostic, opportunity map, 90-day pilot scope. ITMTB." />
      <meta name="twitter:image" content="https://www.itmtb.com/logo.png" />
      <link rel="canonical" href="https://www.itmtb.com/ai-strategy-for-business" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaGraph) }}
      />
    </Head>

    {/* ── HERO ── */}
    <section className="bg-[#1a110a] text-white px-6 pt-36 pb-28">
      <div className="max-w-6xl mx-auto flex flex-col lg:flex-row lg:items-center lg:gap-16">
        <div className="lg:flex-1">
          <SectionEyebrow label="AI strategy for business" />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.06] tracking-tight mb-6 max-w-3xl">
            AI strategy written by people<br className="hidden sm:block" /> who ship the agents.
          </h1>
          <p className="text-lg sm:text-xl text-slate-400 leading-relaxed mb-4 max-w-2xl">
            Two-week executive diagnostic. An AI opportunity map ranked by ROI. A 90-day pilot scope you can hand to engineering. And if you want, we build it too.
          </p>
          <p className="text-base text-slate-500 mb-10 max-w-xl">
            For CEOs, COOs, and CXOs who need a roadmap that survives contact with engineering.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link href="/contact-us" legacyBehavior>
              <a className="inline-block bg-white text-slate-900 hover:bg-slate-100 px-7 py-3.5 text-sm font-semibold rounded tracking-wide transition-colors">
                Book the diagnostic
              </a>
            </Link>
            <a
              href="#industry-intelligence"
              className="inline-block border border-slate-700 text-slate-300 hover:border-slate-500 hover:text-white px-7 py-3.5 text-sm font-semibold rounded tracking-wide transition-colors"
            >
              See what peers are deploying
            </a>
          </div>
        </div>
        <div className="hidden lg:flex lg:w-[420px] shrink-0 items-center justify-center mt-12 lg:mt-0">
          <RoadmapSVG />
        </div>
      </div>
    </section>

    {/* ── CLIENT LOGOS ── */}
    <section className="bg-white py-8 px-6">
      <CustomerSection title="Trusted by" />
    </section>

    {/* ── THE STRATEGIC GAP ── */}
    <section className="bg-white py-20 sm:py-28 px-6">
      <div className="max-w-4xl mx-auto">
        <SectionEyebrow label="The problem" />
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mb-10">
          Most AI strategy decks die on contact with engineering.
        </h2>
        <div className="space-y-8">
          <div className="flex gap-8">
            <span className="text-3xl font-bold text-slate-200 flex-shrink-0 w-8 leading-none">1</span>
            <div>
              <h3 className="text-base font-semibold text-slate-900 mb-2">Advisors who don't build produce recommendations that don't ship.</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                The typical AI strategy engagement produces a roadmap that looks sound in a board deck and falls apart when engineering asks: where does this agent get its data, how does it connect to our ERP, and who handles the compliance review? Strategy advisors who have never shipped an agent in production cannot answer those questions.
              </p>
            </div>
          </div>
          <div className="flex gap-8">
            <span className="text-3xl font-bold text-slate-200 flex-shrink-0 w-8 leading-none">2</span>
            <div>
              <h3 className="text-base font-semibold text-slate-900 mb-2">Build vendors who skip strategy produce agents that solve the wrong problem.</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                The typical custom AI build engagement starts with a brief, moves to a prototype, and delivers something technically functional that the business cannot adopt — because nobody mapped the workflow, the ownership, or the change management before the first line of code was written.
              </p>
            </div>
          </div>
          <div className="flex gap-8">
            <span className="text-3xl font-bold text-slate-200 flex-shrink-0 w-8 leading-none">3</span>
            <div>
              <h3 className="text-base font-semibold text-slate-900 mb-2">ITMTB does both — in the same engagement, with the same team.</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Our diagnostic is written by engineers and architects who have shipped agentic AI in production — not by generalists with an AI toolkit. The roadmap we produce is technically validated before it leaves our hands. If you continue to the build, the same team carries the delivery risk.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ── INDUSTRY INTELLIGENCE (CENTERPIECE) ── */}
    <section id="industry-intelligence" className="bg-slate-50 py-20 sm:py-28 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-14">
          <SectionEyebrow label="What your peers are deploying" />
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mb-3">
            What industry leaders are doing with AI in 2025–2026
          </h2>
          <p className="text-slate-600 text-base max-w-2xl">
            Named, public deployments — not generic case studies. Each section closes with the mid-market pattern we see in actual engagements. For the wider shifts these deployments sit inside, see{" "}
            <Link href="/blog/technology-trends-india-2026" legacyBehavior>
              <a className="text-amber-700 font-medium hover:underline">the eight technology shifts Indian enterprises should act on in 2026</a>
            </Link>
            .
          </p>
        </div>

        <div className="border-t border-slate-200">
          {industryIntelligence.map((ind, i) => (
            <div key={ind.name} className="border-b border-slate-200">
              <button
                onClick={() => setOpenIndustry(openIndustry === i ? null : i)}
                className="w-full flex justify-between items-center gap-4 text-left py-5 focus:outline-none group"
              >
                <span className="text-sm font-bold text-slate-900 uppercase tracking-wide">{ind.name}</span>
                <span className="flex-shrink-0 text-slate-400 group-hover:text-slate-600 transition-colors text-xl leading-none">
                  {openIndustry === i ? "−" : "+"}
                </span>
              </button>
              <div
                className="overflow-hidden transition-all duration-300 ease-in-out"
                style={{ maxHeight: openIndustry === i ? "3000px" : "0px", opacity: openIndustry === i ? 1 : 0 }}
              >
                <div className="pb-8 flex flex-col lg:flex-row gap-8 lg:gap-16">
                  <div className="lg:w-52 flex-shrink-0">
                    {ind.link && (
                      <Link href={ind.link} legacyBehavior>
                        <a className="text-xs font-medium text-slate-500 border-b border-transparent hover:border-slate-500 pb-px transition-colors uppercase tracking-wide">
                          Our work in this sector
                        </a>
                      </Link>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="space-y-5 mb-6">
                      {ind.examples.map((ex) => (
                        <div key={ex.company}>
                          <span className="text-sm font-semibold text-slate-900">{ex.company} — </span>
                          <span className="text-sm text-slate-600 leading-relaxed">{ex.description}</span>
                        </div>
                      ))}
                    </div>
                    <div className="border-l-2 border-amber-700 pl-4 bg-white py-3 pr-4 rounded-r">
                      <span className="text-xs font-semibold uppercase tracking-widest text-amber-700">ITMTB pattern — </span>
                      <span className="text-xs text-slate-700 leading-relaxed">{ind.itmtbPattern}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── WHAT A DIAGNOSTIC PRODUCES ── */}
    <section className="bg-white py-20 sm:py-28 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-12">
          <SectionEyebrow label="What we deliver" />
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mb-3">
            What a two-week diagnostic produces
          </h2>
          <p className="text-slate-600 text-base max-w-2xl">
            Four documents, not a slide deck. Scored against your specific business model, tech stack, and regulatory posture.
          </p>
        </div>
        <div className="space-y-0">
          {deliverables.map((d, i) => (
            <div key={d.number} className={`flex gap-8 py-8 ${i < deliverables.length - 1 ? "border-b border-slate-100" : ""}`}>
              <span className="text-3xl font-bold text-slate-200 flex-shrink-0 w-12 leading-none">{d.number}</span>
              <div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">{d.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed max-w-2xl">{d.body}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-12 pt-8 border-t border-slate-100">
          <Link href="/itmtb-how-we-work" legacyBehavior>
            <a className="text-sm font-medium text-slate-900 border-b border-transparent hover:border-slate-900 pb-px transition-colors">
              Full methodology and engagement options
            </a>
          </Link>
        </div>
      </div>
    </section>

    {/* ── HOW WE DIFFER ── */}
    <section className="bg-slate-50 py-20 sm:py-28 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-12">
          <SectionEyebrow label="Why ITMTB" />
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mb-3">
            Strategy from people who've shipped — not slideware.
          </h2>
        </div>
        <div className="space-y-0">
          {differentiators.map((d, i) => (
            <div key={d.claim} className={`flex gap-8 py-8 ${i < differentiators.length - 1 ? "border-b border-slate-200" : ""}`}>
              <div className="w-2 h-2 rounded-full bg-slate-900 flex-shrink-0 mt-2"></div>
              <div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">{d.claim}</h3>
                <p className="text-sm text-slate-600 leading-relaxed max-w-2xl">{d.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── PROOF — FLAGSHIPS RE-FRAMED ── */}
    <section className="bg-white py-20 sm:py-28 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-12">
          <SectionEyebrow label="Proof" />
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mb-3">
            Strategy that became code that shipped.
          </h2>
          <p className="text-slate-600 text-base max-w-2xl">
            Three ITMTB engagements — each starting with a diagnostic, each shipping in production.
          </p>
        </div>
        <div className="space-y-0">
          {proofCases.map((c, i) => (
            <div key={c.label} className={`py-10 ${i < proofCases.length - 1 ? "border-b border-slate-100" : ""}`}>
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-5">{c.label}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-2">Problem</p>
                  <p className="text-sm text-slate-600 leading-relaxed">{c.problem}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-2">Approach</p>
                  <p className="text-sm text-slate-600 leading-relaxed">{c.approach}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-2">Outcome</p>
                  <p className="text-sm text-slate-900 font-medium leading-relaxed mb-3">{c.outcome}</p>
                  <Link href={c.link} legacyBehavior>
                    <a className="text-xs font-medium text-slate-900 border-b border-transparent hover:border-slate-900 pb-px transition-colors">
                      {c.label === "Managed services" ? "See Orchestrik" : "Read more"}
                    </a>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── CROSS-LINK TO DEPLOYMENT TRACK ── */}
    <div className="bg-white border-t border-b border-slate-200">
      <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
        <div>
          <p className="text-amber-700 text-xs font-semibold uppercase tracking-widest mb-1">For a different intent</p>
          <p className="text-slate-900 text-lg font-semibold leading-snug">Already know what to build?</p>
          <p className="text-slate-500 text-sm mt-1">Two-week architecture sprint → production agent build. No strategy track needed.</p>
        </div>
        <Link href="/agentic-ai" legacyBehavior>
          <a className="flex-shrink-0 border border-slate-300 text-slate-900 hover:bg-slate-50 px-6 py-3 text-sm font-semibold rounded transition-colors whitespace-nowrap">
            See Agentic AI Services
          </a>
        </Link>
      </div>
    </div>

    {/* ── GOVERNANCE & RISK ── */}
    <section className="bg-slate-50 py-20 sm:py-28 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <SectionEyebrow label="Governance & risk" />
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mb-3">
            AI governance, addressed from day one.
          </h2>
          <p className="text-slate-600 text-base max-w-2xl">
            Governance is scoped to risk, not applied uniformly. We document the posture per workflow — not a blanket policy.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {governanceAnchors.map((a) => (
            <div key={a.title} className="border border-slate-200 rounded-lg p-6 bg-white">
              <h3 className="text-sm font-bold text-slate-900 mb-3">{a.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{a.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-8">
          <Link href="/agentic-ai#regulatory-posture" legacyBehavior>
            <a className="text-sm font-medium text-slate-900 border-b border-transparent hover:border-slate-900 pb-px transition-colors">
              Full regulatory posture detail
            </a>
          </Link>
        </div>
      </div>
    </section>

    {/* ── READINESS ASSESSMENT ── */}
    <section className="bg-white py-20 sm:py-28 px-6">
      <div className="max-w-4xl mx-auto">
        <SectionEyebrow label="Readiness assessment" />
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mb-4">
          Not sure if you're ready yet?
        </h2>
        <p className="text-slate-600 text-base leading-relaxed mb-8 max-w-2xl">
          Take the Agentic AI Readiness Assessment before booking the diagnostic. Six dimensions — data infrastructure, process maturity, technical compatibility, organisational alignment, regulatory posture, and change management — in three minutes. Instant report.
        </p>
        <Link href="/tools/agentic-ai-readiness-quiz" legacyBehavior>
          <a className="inline-block bg-slate-900 text-white hover:bg-slate-800 px-7 py-3.5 text-sm font-semibold rounded tracking-wide transition-colors">
            Take the assessment
          </a>
        </Link>
      </div>
    </section>

    {/* ── BOOKING CTA ── */}
    <div className="bg-white">
      <ContactUsHome />
    </div>

    {/* ── FAQ ── */}
    <section className="bg-white py-20 sm:py-28 px-6 border-t border-slate-100">
      <div className="max-w-4xl mx-auto">
        <div className="mb-10">
          <SectionEyebrow label="FAQ" />
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
            What CXOs ask before engaging
          </h2>
        </div>
        <FaqAccordion items={faqItems} />
      </div>
    </section>

    {/* ── RELATED INSIGHTS ── */}
    <section className="bg-white py-16 px-6 border-t border-slate-100">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-10">Further reading</h2>
        <BlogSlider blogs={relatedPosts} />
      </div>
    </section>

    <Footer />
  </div>
  );
};

AIStrategyPage.title = "AI Strategy for Business | ITMTB";
AIStrategyPage.desc = "Book an AI strategy session with ITMTB. We help enterprises plan and deploy AI agents — from scoping to production.";

export default AIStrategyPage;
